import { useState } from "react";
import { useSnackbar } from "notistack";
import { addSkill, deleteSkill, updateSkill } from "@endpoints/skill.service";
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { JOB_STEPS } from "../CreateJobPage";
import CustomInputV2 from "@components/CustomInputV2/CustomInputV2";
import { ArrowDown, PlusIcon } from "lucide-react";
import CustomDropdown from "./CustomDropdown";
import FieldError from "./FieldError";
import {
  EMPTY_SKILL,
  JobFormValues,
  JobSkillFormValues,
  SKILL_INPUT_FIELDS,
} from "../schema/jobForm.schema";
import { toAddSkillPayload, toUpdateSkillPayload } from "../lib/skillPayload";
import { useCreateJob } from "../hooks/useCreateJobWizard";
import {
  formatSkillValue,
  Option,
  SKILL_EXPERIENCE_OPTIONS,
  SKILL_IMPORTANCE_OPTIONS,
  SKILL_LEVEL_OPTIONS,
  SKILL_RECENCY_OPTIONS,
} from "../schema/jobForm.options";

const SKILL_SELECT_FIELDS: {
  name: Exclude<
    keyof JobSkillFormValues,
    "_id" | "title" | "howWouldYouLikeToUseThisSkill" | "isSaved"
  >;
  label: string;
  options: string[] | Option<number>[];
}[] = [
  { name: "importance", label: "Importance", options: SKILL_IMPORTANCE_OPTIONS },
  { name: "requiredLevel", label: "Required level", options: SKILL_LEVEL_OPTIONS },
  { name: 'years', label: "Minimum experience", options: SKILL_EXPERIENCE_OPTIONS },
  { name: 'lastUsed', label: "Recency", options: SKILL_RECENCY_OPTIONS },
];

interface SkillCardProps {
  skillIndex: number;
  onRemove: () => void;
}

const SkillCard = ({ skillIndex, onRemove }: SkillCardProps) => {
  const { ensureJobId } = useCreateJob();
  const { enqueueSnackbar } = useSnackbar();
  const {
    control,
    register,
    getValues,
    setValue,
    trigger,
    clearErrors,
    formState: { errors },
  } = useFormContext<JobFormValues>();
  const skill = useWatch({ control, name: `skills.${skillIndex}` });
  const skillErrors = errors.skills?.[skillIndex];

  // Unsaved skills (new, imported, or being edited) show the edit form, and so
  // do saved ones whose fields fail validation on Continue.
  const hasFieldErrors = SKILL_INPUT_FIELDS.some((name) => skillErrors?.[name]);
  const isEditing = !skill?.isSaved || hasFieldErrors;
  const [isSkillUsedExpanded, setIsSkillUsedExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSave = async () => {
    const isValid = await trigger(
      SKILL_INPUT_FIELDS.map((name) => `skills.${skillIndex}.${name}` as const),
      { shouldFocus: true },
    );
    if (!isValid) return;

    const current = getValues(`skills.${skillIndex}`);
    try {
      setIsSubmitting(true);
      if (current._id) {
        const { success } = await updateSkill(toUpdateSkillPayload(current));
        if (!success) throw new Error("Could not update the skill");
      } else {
        const jobId = await ensureJobId();
        const { success, data } = await addSkill(
          toAddSkillPayload(current, jobId),
        );
        if (!success || !data?._id) throw new Error("Could not add the skill");
        setValue(`skills.${skillIndex}._id`, data._id);
      }
      setValue(`skills.${skillIndex}.isSaved`, true);
      clearErrors(`skills.${skillIndex}.isSaved`);
    } catch (error) {
      enqueueSnackbar(error?.message ?? String(error), { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = () => {
    setValue(`skills.${skillIndex}.isSaved`, false);
  };

  // Saved skills are deleted on the server before leaving the list.
  const handleRemove = async () => {
    const skillId = getValues(`skills.${skillIndex}._id`);
    if (!skillId) return onRemove();

    try {
      setIsSubmitting(true);
      const { success } = await deleteSkill(skillId);
      if (!success) throw new Error("Could not remove the skill");
      onRemove();
    } catch (error) {
      enqueueSnackbar(error?.message ?? String(error), { variant: "error" });
      setIsSubmitting(false);
    }
  };

  //   Create/Edit
  if (isEditing)
    return (
      <div className="rounded-md border-l-[3px] border-r border-y border-l-slate-600 overflow-hidden">
        <div className="flex justify-between py-3 border-b px-4 bg-slate-100">
          <div className="flex items-center gap-4">
            <span className="text-xss font-bold text-light-black uppercase">
              {String(skillIndex + 1)?.padStart(2, "0")}
            </span>
            <div className="flex flex-col gap-0.5">
              <span className="text-xsss font-bold tracking-wider text-light-black">
                SKILL REQUIREMENT
              </span>
              <CustomInputV2
                placeholder="e.g. Cloud security"
                inputContainerStyle={
                  skillErrors?.title ? "border-orange-700" : ""
                }
                {...register(`skills.${skillIndex}.title`)}
              />
              <FieldError message={skillErrors?.title?.message} />
            </div>
          </div>
          <div className="flex items-center gap-4">
            {skill?.importance ? (
              <div className="rounded-full px-2 py-1 text-light-black bg-gray-200 text-xsss font-bold">
                {skill.importance}
              </div>
            ) : null}
            <FieldError message={skillErrors?.isSaved?.message} />
            <button
              type="button"
              onClick={handleSave}
              disabled={isSubmitting}
              className="border border-light-grey bg-gray-200 rounded-md font-bold text-xsss text-light-black px-3 hover:bg-slate-50 h-8 disabled:opacity-50"
            >
              {isSubmitting ? "Saving..." : "Save Skill"}
            </button>
            <button
              type="button"
              onClick={handleRemove}
              disabled={isSubmitting}
              className="font-bold text-xsss text-light-black hover:bg-slate-50 rounded-md px-3 h-8 disabled:opacity-50"
            >
              Remove
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-4 px-4 py-3">
          <div className="grid grid-cols-4 gap-4 items-start">
            {SKILL_SELECT_FIELDS.map(({ name, label, options }) => (
              <div key={name} className="flex flex-col gap-1.5">
                <label className="text-xss font-bold text-light-black">
                  {label}
                </label>
                <Controller
                  control={control}
                  name={`skills.${skillIndex}.${name}`}
                  render={({ field, fieldState }) => (
                    <>
                      <CustomDropdown
                        options={options}
                        value={field.value}
                        onChange={field.onChange}
                        hasError={!!fieldState.error}
                      />
                      <FieldError message={fieldState.error?.message} />
                    </>
                  )}
                />
              </div>
            ))}
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xss font-bold text-light-black">
              How will this skill be used in the role?
            </label>
            <textarea
              className={`border rounded-md text-xss font-medium text-charcoal p-2 ${skillErrors?.howWouldYouLikeToUseThisSkill ? "border-orange-700" : ""}`}
              placeholder="Describe the decisions, responsibilities or outcomes this skill supports."
              rows={5}
              {...register(`skills.${skillIndex}.howWouldYouLikeToUseThisSkill`)}
            />
            <FieldError message={skillErrors?.howWouldYouLikeToUseThisSkill?.message} />
          </div>
        </div>
      </div>
    );

  //   Read only
  return (
    <div className="rounded-md border-l-[3px] border-r border-y border-l-slate-600 overflow-hidden py-3.5 px-4">
      <div className="flex justify-between pb-2.5">
        <div className="flex flex-col">
          <span className="text-xsss font-bold text-light-black uppercase">
            {String(skillIndex + 1)?.padStart(2, "0")} / SKILL REQUIREMENT
          </span>
          <span className="text-md font-semibold text-black">
            {skill?.title}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleEdit}
            disabled={isSubmitting}
            className="border border-slate-600 rounded-md font-bold text-xsss text-slate-600 px-3 hover:bg-slate-50 h-8 disabled:opacity-50"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={handleRemove}
            disabled={isSubmitting}
            className="font-bold text-xsss text-light-black hover:bg-slate-50 rounded-md px-3 h-8 disabled:opacity-50"
          >
            {isSubmitting ? "Removing..." : "Remove"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-[0.9fr_0.9fr_0.9fr_0.9fr_1.4fr] items-center border-y py-2.5">
        {SKILL_SELECT_FIELDS.map(({ name, label }, index) => (
          <div
            key={name}
            className={`flex flex-col flex-1 gap-0.5 ${index > 0 ? "border-l pl-4" : ""}`}
          >
            <span className="font-bold text-xsss tracking-wider uppercase text-light-black">
              {label}
            </span>
            <span className="text-xss font-bold text-black">
              {formatSkillValue(skill, name)}
            </span>
          </div>
        ))}

        <button
          type="button"
          onClick={() => setIsSkillUsedExpanded((prev) => !prev)}
          aria-expanded={isSkillUsedExpanded}
          className={`flex justify-between items-center ml-auto w-max gap-8 px-2 py-1 border rounded-md ${isSkillUsedExpanded ? "bg-slate-100 border-light-grey" : ""} hover:bg-slate-100`}
        >
          <div className="flex flex-col gap-0.5">
            <span className="font-bold text-xsss tracking-wider uppercase text-light-black text-left">
              Role-use Context
            </span>
            <span className="text-xsss font-bold text-black text-left">
              {isSkillUsedExpanded ? "Hide full text" : "View full text"}
            </span>
          </div>

          <div className="flex items-center justify-center w-6 h-6 rounded-full border bg-white shrink-0">
            <ArrowDown
              size={12}
              className={`transition-transform duration-200 ease-out ${
                isSkillUsedExpanded ? "rotate-180" : "rotate-0"
              }`}
            />
          </div>
        </button>
      </div>

      {isSkillUsedExpanded ? (
        <div className="flex flex-col gap-1 p-4 mt-3 border-l-[3px] border-y border-r border-gray-200 rounded-r-md border-l-slate-600 bg-slate-50">
          <span className="font-bold text-xsss text-light-black tracking-wider uppercase">
            How this skill is used in the role
          </span>
          <span className="text-xss font-medium text-charcoal leading-4">
            {skill?.howWouldYouLikeToUseThisSkill}
          </span>
        </div>
      ) : null}
    </div>
  );
};

const Step4 = ({ activeStep }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<JobFormValues>();
  const { fields, append, remove } = useFieldArray({ control, name: "skills" });

  console.log('This is skills',fields)

  return (
    <>
      {/* Header */}
      <div className="flex border-b pb-3">
        <div className="flex flex-col gap-0.5 flex-1">
          <span className="text-xss font-bold text-light-black uppercase">
            Step {String(activeStep).padStart(2, "0")} /{" "}
            {JOB_STEPS[activeStep - 1]?.label}
          </span>
          <span className="font-semibold text-xl">
            {JOB_STEPS[activeStep - 1]?.title}
          </span>
          <span className="text-xss font-medium text-light-black">
            {JOB_STEPS[activeStep - 1]?.subtitle}
          </span>
        </div>
        <span className="self-end text-xss font-bold text-light-black uppercase leading-3">
          {fields.length} requirement{fields.length === 1 ? "" : "s"}
        </span>
      </div>

      <div className="flex flex-col mt-5 gap-3">
        {fields.map((field, skillIndex) => {
          return (
            <SkillCard
              key={field.id}
              skillIndex={skillIndex}
              onRemove={() => remove(skillIndex)}
            />
          );
        })}
        <FieldError
          message={errors.skills?.root?.message ?? errors.skills?.message}
        />
        <button
          type="button"
          onClick={() => append({ ...EMPTY_SKILL })}
          className="flex items-center rounded-md border border-dashed bg-slate-50 font-bold text-slate-600 text-xss h-10 w-max px-3 gap-2 hover:bg-white"
        >
          <PlusIcon size={14} />
          Add another skill requirement
        </button>
      </div>
    </>
  );
};

export default Step4;
